// Vale: dinheiro que a empresa adianta ao colaborador (quase sempre ao
// motorista, na viagem). Recibo em PDF feito no aparelho e assinatura de
// dois jeitos: na tela de quem paga, ou por um link que a pessoa abre no
// celular dela, sem login.
import {
  codigoDoLink, colaboradores, guardarCodigoDoLink, situacoesDeEnvio, vales, veiculos, viagens,
} from '../dados.js';
import { puxarAgora } from '../local/sincronia.js';
import { assinaturaSuficiente, caminhoDosTracos, desenhoDaAssinatura, novoCodigo, quadroDeAssinatura, sha256Hex } from '../lib/assinatura.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { reaisPorExtenso } from '../lib/extenso.js';
import { data, dinheiro, lerNumero, numeroBR, placa as formatarPlaca } from '../lib/formato.js';
import { entregarArquivo, nomeDoArquivo, reciboEmPdf } from '../lib/recibo.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, confirmar, dialogo, emblema, faixa, formulario, secao, vazio } from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';

export const FORMAS_VALE = [['pix', 'Pix'], ['dinheiro', 'Dinheiro'], ['transferencia', 'Transferência'], ['outro', 'Outro']];
const NOME_FORMA = Object.fromEntries(FORMAS_VALE);
const VALIDADE_LINK_DIAS = 30;

function horaLocal(iso) {
  if (!iso) return '';
  const fuso = sessao.empresa?.fuso_horario || 'America/Sao_Paulo';
  const quando = new Date(iso);
  return `${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(quando)} às ${
    new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(quando)}`;
}

const primeiroNome = (c) => (c?.apelido || c?.nome || '').split(' ')[0];

// Endereço do link de assinatura (o do próprio app).
export function enderecoDoLink(codigo) {
  return `${location.origin}${location.pathname}#/assinar/${codigo}`;
}

// ---------------------------------------------------------------------
// Tela do vale (novo, ver e editar)
// ---------------------------------------------------------------------
export function telaVale({ params, busca }) {
  const tela = nestaTela();
  const novo = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, novo ? 'Novo vale' : 'Vale');
  const pagina = casca({ titulo, voltar: '/viagens', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const vale = novo ? null : await vales.obter(params.id);
    if (!novo && !vale) {
      trocar(corpo, vazio({ titulo: 'Vale não encontrado', texto: 'Ele pode ter sido excluído.', acao: h('a', { class: 'botao', href: '#/viagens' }, 'Ver viagens') }));
      return;
    }
    const [pessoas, listaViagens, frota, envio] = await Promise.all([
      colaboradores.listar(), viagens.listar(), veiculos.listar(), situacoesDeEnvio('lancamentos'),
    ]);
    const hoje = hojeDaEmpresa();
    const viagemPedida = listaViagens.find((x) => x.id === (vale?.viagem_id ?? busca.get('viagem')));
    const colaboradorInicial = vale?.colaborador_id ?? busca.get('colaborador') ?? viagemPedida?.motorista_id ?? '';
    const voltar = viagemPedida ? `/viagens/${viagemPedida.id}` : colaboradorInicial ? `/colaboradores/${colaboradorInicial}` : '/viagens';
    linkVoltar?.setAttribute('href', `#${voltar}`);
    const ativos = pessoas.filter((p) => p.ativo || p.id === vale?.colaborador_id);
    // Vale do histórico (Comissões): já foi acertado nos programas antigos.
    const importado = vale?.origem === 'importacao';
    const gravavel = podeGravar() && !(vale?.acerto_id) && !importado;
    const caminhao = (viagem) => frota.find((x) => x.id === viagem?.veiculo_id);
    const textoViagem = (x) => `Viagem de ${data(x.data_saida)}${caminhao(x) ? ` · ${formatarPlaca(caminhao(x).placa)}` : ''}${x.status === 'aberta' ? ' (em andamento)' : ''}`;

    // Viagens de quem recebe: as em andamento e as mais recentes.
    const opcoesViagem = (colaboradorId) => {
      const dele = listaViagens.filter((x) => x.motorista_id === colaboradorId);
      const lista = [...dele.filter((x) => x.status === 'aberta'), ...dele.filter((x) => x.status !== 'aberta').slice(0, 8)];
      if (vale?.viagem_id && !lista.some((x) => x.id === vale.viagem_id)) {
        const atual = listaViagens.find((x) => x.id === vale.viagem_id);
        if (atual) lista.push(atual);
      }
      return [['', 'Sem viagem'], ...lista.map((x) => [x.id, textoViagem(x)])];
    };
    const viagemPadrao = (colaboradorId) => {
      if (viagemPedida && viagemPedida.motorista_id === colaboradorId) return viagemPedida.id;
      return listaViagens.find((x) => x.motorista_id === colaboradorId && x.status === 'aberta')?.id ?? '';
    };

    const seletorViagem = campo({
      rotulo: 'Viagem', nome: 'viagem_id', tipo: 'select',
      valor: vale ? vale.viagem_id ?? '' : viagemPadrao(colaboradorInicial),
      opcoes: opcoesViagem(colaboradorInicial),
      dica: 'O vale entra no acerto do motorista desta viagem.',
    });

    const form = formulario({
      filhos: [
        campo({
          rotulo: 'Para quem', nome: 'colaborador_id', tipo: 'select', obrigatorio: true, valor: colaboradorInicial,
          opcoes: [['', 'Escolha a pessoa'], ...ativos.map((p) => [p.id, p.apelido ? `${p.nome} (${p.apelido})` : p.nome])],
          aoMudar: (e) => {
            const sel = seletorViagem.querySelector('select');
            sel.replaceChildren(...opcoesViagem(e.target.value).map(([valor, texto]) => h('option', { value: valor }, texto)));
            sel.value = viagemPadrao(e.target.value);
          },
        }),
        seletorViagem,
        h('div', { class: 'grade-2' },
          campo({ rotulo: 'Valor (R$)', nome: 'valor', obrigatorio: true, formato: 'dinheiro', valor: vale ? numeroBR(vale.valor, { casas: 2 }) : '', atributos: { inputmode: 'decimal' } }),
          campo({ rotulo: 'Data', nome: 'data', tipo: 'date', obrigatorio: true, valor: vale?.data ?? hoje })),
        campo({ rotulo: 'Como foi pago', nome: 'forma', tipo: 'segmentos', valor: vale?.forma ?? 'pix', opcoes: FORMAS_VALE }),
        campo({ rotulo: 'Observação', nome: 'descricao', tipo: 'textarea', valor: vale?.descricao ?? '', dica: 'Aparece no recibo.' }),
      ],
      textoBotao: novo ? 'Salvar vale' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (!d.colaborador_id) erros.colaborador_id = 'Escolha para quem é o vale.';
        const valor = lerNumero(d.valor);
        if (!(valor > 0)) erros.valor = 'Informe o valor, como 1.000,00.';
        if (!d.data) erros.data = 'Informe a data.';
        const viagem = listaViagens.find((x) => x.id === d.viagem_id);
        if (viagem && d.data && d.data < viagem.data_saida) erros.data = `O vale é da viagem que saiu em ${data(viagem.data_saida)}: a data não pode ser antes.`;
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          colaborador_id: d.colaborador_id,
          viagem_id: d.viagem_id || null,
          valor: lerNumero(d.valor),
          data: d.data,
          forma: d.forma || 'pix',
          descricao: d.descricao || null,
        };
        if (novo) {
          const criado = await vales.criar(dados);
          avisar('Vale salvo. Agora mande o recibo ou peça a assinatura.');
          tela.ir(`/vales/${criado.id}`, { substituir: true });
          return;
        }
        const mudaAssinado = vale.assinatura && (
          dados.valor !== Number(vale.valor) || dados.data !== vale.data || dados.forma !== vale.forma || dados.colaborador_id !== vale.colaborador_id);
        if (mudaAssinado) {
          const ok = await confirmar({
            titulo: 'Este vale está assinado',
            texto: 'Mudar o valor, a data, a forma ou a pessoa apaga a assinatura. Depois ela precisa assinar de novo.',
            sim: 'Mudar e apagar a assinatura',
            perigo: true,
          });
          if (!ok) return;
          Object.assign(dados, { assinatura: null, assinado_em: null, assinado_via: null, assinatura_token_hash: null });
        }
        await vales.atualizar(vale.id, dados, vale);
        avisar('Alterações salvas.');
        tela.renderizar();
      },
      extras: !novo && gravavel && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({ titulo: 'Excluir este vale?', texto: 'Use só para vale lançado por engano. Ele vai para a lixeira e o link de assinatura deixa de valer.', sim: 'Excluir', perigo: true });
          if (!ok) return;
          try {
            await vales.excluir(vale.id);
            avisar('Vale excluído.');
            tela.ir(voltar, { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Excluir vale lançado por engano'),
    });

    if (novo) {
      trocar(corpo, form);
      return;
    }

    titulo.textContent = `Vale · ${dinheiro(vale.valor)}`;
    const colaborador = pessoas.find((p) => p.id === vale.colaborador_id);
    const viagem = listaViagens.find((x) => x.id === vale.viagem_id) ?? null;
    trocar(corpo,
      vale.acerto_id && faixa('Este vale já entrou num acerto fechado: não muda mais.', 'info'),
      importado && faixa('Este vale veio do Comissões (programas antigos) e já foi acertado lá: fica só no histórico e não entra em acerto.', 'info'),
      await blocoRecibo({ vale, colaborador, viagem, veiculo: caminhao(viagem), envio: envio.get(vale.id), gravavel, tela }),
      secao('Dados do vale', form),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return pagina;
}

// ---------------------------------------------------------------------
// Recibo e assinatura
// ---------------------------------------------------------------------
async function blocoRecibo({ vale, colaborador, viagem, veiculo, envio, gravavel, tela }) {
  const registro = await codigoDoLink(vale.id);
  const linkPedido = Boolean(vale.assinatura_token_hash);
  const faltaEnviar = envio === 'pendente' || envio === 'recusada';

  const executar = async (acao, sucesso) => {
    try {
      await acao();
      if (sucesso) avisar(sucesso);
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  const recibo = async (botao) => {
    botao.disabled = true;
    try {
      const blob = await reciboEmPdf({ vale, empresa: sessao.empresa, colaborador, viagem, veiculo });
      const resultado = await entregarArquivo(blob, nomeDoArquivo({ vale, colaborador }), `Recibo do vale de ${primeiroNome(colaborador)}`);
      if (resultado === 'baixado') avisar('Recibo baixado em PDF.');
    } catch (erro) {
      console.error(erro);
      avisar('Não foi possível gerar o PDF agora. Tente de novo.', 'erro');
    } finally {
      botao.disabled = false;
    }
  };

  // Mesmo link de antes, se ainda vale; senão, um novo (o antigo deixa de valer).
  const link = async () => {
    const vencido = vale.assinatura_pedida_em
      && Date.now() - new Date(vale.assinatura_pedida_em).getTime() > (VALIDADE_LINK_DIAS - 1) * 86400000;
    if (registro && registro.hash === vale.assinatura_token_hash && !vencido) return enderecoDoLink(registro.codigo);
    const codigo = novoCodigo();
    const hash = await sha256Hex(codigo);
    await guardarCodigoDoLink(vale.id, { codigo, hash, criado_em: new Date().toISOString() });
    // Sem esperar o servidor: o WhatsApp precisa abrir logo depois do toque
    // (senão o navegador bloqueia). O vale sobe logo em seguida.
    await vales.atualizar(vale.id, { assinatura_token_hash: hash }, null, { esperarServidor: false });
    return enderecoDoLink(codigo);
  };

  const mensagem = (url) =>
    `Olá, ${primeiroNome(colaborador)}! Registrei um vale de ${dinheiro(vale.valor)} em ${data(vale.data)}${vale.forma && vale.forma !== 'outro' ? ` (${NOME_FORMA[vale.forma].toLowerCase()})` : ''}. `
    + `Confira e assine pelo link: ${url}`;

  const pedirPeloWhatsApp = () => executar(async () => {
    const url = await link();
    const texto = encodeURIComponent(mensagem(url));
    const destino = colaborador?.celular ? `https://wa.me/${colaborador.celular}?text=${texto}` : `https://wa.me/?text=${texto}`;
    const a = h('a', { href: destino, target: '_blank', rel: 'noopener' });
    document.body.append(a);
    a.click();
    a.remove();
  }, null);

  const copiarLink = () => executar(async () => {
    const url = await link();
    try {
      await navigator.clipboard.writeText(url);
      avisar('Link copiado. Cole na conversa com a pessoa.');
    } catch {
      await dialogo((fechar) => [
        h('h2', null, 'Link para assinar'),
        h('p', null, 'Copie e mande para a pessoa:'),
        h('input', { type: 'text', value: url, readOnly: true, class: 'link-copiar', onFocus: (e) => e.target.select() }),
        h('div', { class: 'acoes' }, h('button', { class: 'botao cheio', type: 'button', onClick: () => fechar(null) }, 'Fechar')),
      ]);
    }
  }, null);

  const assinarNaTela = async () => {
    const caminho = await dialogoAssinar(vale, colaborador);
    if (caminho) {
      executar(() => vales.atualizar(vale.id, { assinatura: caminho, assinado_em: new Date().toISOString(), assinado_via: 'presencial' }), 'Assinatura guardada.');
    }
  };

  const apagarAssinatura = async () => {
    const ok = await confirmar({
      titulo: 'Apagar a assinatura?',
      texto: 'Use quando o vale estiver errado: depois de corrigir, a pessoa assina de novo. O link antigo deixa de valer.',
      sim: 'Apagar assinatura',
      perigo: true,
    });
    if (ok) executar(() => vales.atualizar(vale.id, { assinatura: null, assinado_em: null, assinado_via: null, assinatura_token_hash: null }), 'Assinatura apagada.');
  };

  let situacao;
  if (vale.assinatura) {
    situacao = [
      desenhoDaAssinatura(vale.assinatura),
      h('p', { class: 'pequeno' }, `${colaborador?.nome ?? ''} assinou ${vale.assinado_via === 'link' ? 'pelo link, no celular dele,' : 'na tela'} em ${horaLocal(vale.assinado_em)}.`),
    ];
  } else if (linkPedido) {
    situacao = faixa(
      faltaEnviar
        ? 'O link já foi feito, mas este vale ainda não chegou ao servidor: o link só abre depois que o sinal voltar e ele for enviado.'
        : `Link mandado${vale.assinatura_pedida_em ? ` em ${horaLocal(vale.assinatura_pedida_em)}` : ''}. Quando a pessoa assinar, a assinatura aparece aqui.`,
      'info', 'Esperando a assinatura');
  } else {
    situacao = h('p', { class: 'pequeno' }, 'Ainda sem assinatura. Se a pessoa estiver aí, ela assina na tela; se estiver longe, mande o link pelo WhatsApp.');
  }

  // Esperando a pessoa assinar pelo link: confere de tempos em tempos (a
  // tela se redesenha sozinha quando a assinatura chega).
  if (linkPedido && !vale.assinatura && !faltaEnviar) {
    const relogio = setInterval(() => {
      if (!bloco.isConnected) {
        clearInterval(relogio);
        return;
      }
      if (document.visibilityState === 'visible') puxarAgora(vale.empresa_id, ['lancamentos']).catch(() => {});
    }, 20000);
  }

  const noComputador = ['localhost', '127.0.0.1'].includes(location.hostname);
  const bloco = secao('Recibo e assinatura', h('div', { class: 'lista' },
    h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'valor-grande' }, dinheiro(vale.valor)),
      h('span', { class: 'cartao-linha' }, reaisPorExtenso(vale.valor)),
      h('span', { class: 'cartao-linha' }, `${colaborador?.nome ?? ''} · ${data(vale.data)} · ${NOME_FORMA[vale.forma] ?? 'Outro'}`),
      h('span', { class: 'cartao-emblemas' },
        vale.assinatura ? emblema('Assinado', 'ok') : linkPedido ? emblema('Link mandado', 'info') : emblema('Sem assinatura'),
        emblemaEnvio(envio)))),
    situacao,
    h('div', { class: 'acoes' },
      h('button', { class: 'botao cheio', type: 'button', onClick: (e) => recibo(e.currentTarget) }, 'Recibo em PDF'),
      gravavel && !vale.assinatura && h('button', { class: 'botao cheio secundario', type: 'button', onClick: assinarNaTela }, 'Assinar na tela agora'),
      gravavel && !vale.assinatura && h('button', { class: 'botao cheio secundario', type: 'button', onClick: pedirPeloWhatsApp }, 'Pedir assinatura pelo WhatsApp'),
      gravavel && !vale.assinatura && h('button', { class: 'botao texto', type: 'button', onClick: copiarLink }, 'Copiar o link de assinatura'),
      gravavel && vale.assinatura && h('button', { class: 'botao texto', type: 'button', onClick: apagarAssinatura }, 'Apagar assinatura')),
    gravavel && !vale.assinatura && noComputador && h('p', { class: 'pequeno' },
      'Atenção: o app ainda roda só neste computador. O link de assinatura só vai abrir no celular da pessoa depois que o app for publicado na internet.')));
  return bloco;
}

// Diálogo para a pessoa assinar na tela de quem paga.
function dialogoAssinar(vale, colaborador) {
  return dialogo((fechar) => {
    const quadro = quadroDeAssinatura();
    const confirmarBotao = h('button', { class: 'botao cheio', type: 'button', disabled: true }, 'Confirmar assinatura');
    quadro.aoMudar(() => {
      confirmarBotao.disabled = !assinaturaSuficiente(quadro.tracos());
    });
    confirmarBotao.addEventListener('click', () => {
      const tracos = quadro.tracos();
      if (!assinaturaSuficiente(tracos)) return;
      quadro.desligar();
      fechar(caminhoDosTracos(tracos));
    });
    return [
      h('h2', null, `Assinatura de ${colaborador?.nome ?? ''}`),
      h('p', null, `Confirmo que recebi ${dinheiro(vale.valor)} (${reaisPorExtenso(vale.valor)}) em ${data(vale.data)}.`),
      h('p', { class: 'pequeno' }, 'Assine com o dedo no quadro:'),
      quadro.elemento,
      h('div', { class: 'acoes' },
        confirmarBotao,
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => quadro.limpar() }, 'Limpar e assinar de novo'),
        h('button', { class: 'botao texto', type: 'button', onClick: () => { quadro.desligar(); fechar(null); } }, 'Cancelar')),
    ];
  });
}
